import { db, auth } from '../firebase';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, query, where, Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';

export const fetchFirestoreEvents = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return { events: [], stats: { total: 0, done: 0, left: 0 } };

    const collectionRef = collection(db, 'lawyers', user.uid, 'events');
    const snapshot = await getDocs(collectionRef);
    //const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const events = snapshot.docs.map(doc => ({ 
      docId: doc.id,  // Change from 'id' to 'docId'
      ...doc.data() 
    }));
    
    let doneCount = 0;
    let leftCount = 0;
    
    // Process events to ensure consistent date format
    // Filter out hidden events (e.g. silent "update next hearing" reminders)
    const visibleEvents = events.filter(event => !event.isHidden);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const processedEvents = visibleEvents.map(event => {
      let eventDate;
      if (event.date instanceof Timestamp) {
        eventDate = event.date.toDate();
      } else if (typeof event.date === 'string') {
        eventDate = new Date(event.date);
      } else {
        eventDate = new Date();
      }

      // Count status for statistics
      if (event.status === 'done') {
        doneCount++;
      } else {
        leftCount++;
      }

      return {
        ...event,
        date: format(eventDate, 'yyyy-MM-dd'),
        formattedDate: format(eventDate, 'yyyy-MM-dd HH:mm a'),
      };
    });

    // Filter out past events (only show today and future)
    const upcomingEvents = processedEvents.filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    });

    return {
      events: upcomingEvents,
      stats: {
        total: upcomingEvents.length,
        done: doneCount,
        left: leftCount
      }
    };
  } catch (error) {
    console.error('Error fetching Firestore events:', error);
    throw error;
  }
};

export const fetchGoogleEventsFromFirestore = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];

    const googleEventsRef = collection(db, 'lawyers', user.uid, 'google_events');
    const snapshot = await getDocs(googleEventsRef);
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const allEvents = snapshot.docs.map(doc => {
      const data = doc.data();
      const eventDate = new Date(data.date);

      return {
        id: doc.id,
        ...data,
        date: format(eventDate, 'yyyy-MM-dd'),
        formattedDate: format(eventDate, 'yyyy-MM-dd HH:mm a'),
      };
    });

    // Filter out past events (only show today and future)
    return allEvents.filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      return eventDate >= today;
    });
  } catch (error) {
    console.error('Error fetching Google events from Firestore:', error);
    throw error;
  }
};

export const addEvent = async (eventData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const eventId = `event_${Date.now()}`;
    const eventsRef = collection(db, 'lawyers', user.uid, 'events');
    const eventDoc = doc(eventsRef, eventId);
    
    const event = {
      ...eventData,
      id: eventId,
      status: 'left',
      isScheduled: false,
      isGoogleEvent: false,
      createdAt: Timestamp.now()
    };
    
    await setDoc(eventDoc, event);
    return eventId;
  } catch (error) {
    console.error('Error adding event:', error);
    throw error;
  }
};

export const updateEvent = async (eventId, eventData) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const eventDoc = doc(db, 'lawyers', user.uid, 'events', eventId);
    await updateDoc(eventDoc, eventData);
    return true;
  } catch (error) {
    console.error('Error updating event:', error);
    throw error;
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const eventDoc = doc(db, 'lawyers', user.uid, 'events', eventId);
    await deleteDoc(eventDoc);
    return true;
  } catch (error) {
    console.error('Error deleting event:', error);
    throw error;
  }
};

export const getEventsByDate = async (date) => {
  try {
    const user = auth.currentUser;
    if (!user) return [];

    const formattedDate = format(new Date(date), 'yyyy-MM-dd');
    
    const eventsRef = collection(db, 'lawyers', user.uid, 'events');
    const eventsQuery = query(eventsRef, where('date', '==', formattedDate));
    const snapshot = await getDocs(eventsQuery);
    
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error getting events by date:', error);
    throw error;
  }
};

export const updateEventIsScheduled = async (eventId, isScheduled) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No authenticated user');

    const eventDoc = doc(db, 'lawyers', user.uid, 'events', eventId);
    await updateDoc(eventDoc, { isScheduled });
    return true;
  } catch (error) {
    console.error('Error updating event schedule status:', error);
    throw error;
  }
};

export const getGoogleEventsByDate = async (date) => {
  try {
    const user = auth.currentUser;
    if (!user) return [];

    const formattedDate = format(new Date(date), 'yyyy-MM-dd');
    
    const eventsRef = collection(db, 'lawyers', user.uid, 'google_events');
    const snapshot = await getDocs(eventsRef);
    
    return snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(event => {
        const eventDate = new Date(event.date);
        const eventDateFormatted = format(eventDate, 'yyyy-MM-dd');
        return eventDateFormatted === formattedDate;
      });
  } catch (error) {
    console.error('Error getting Google events by date:', error);
    throw error;
  }
};